import { useTranslation } from 'react-i18next'
import { useGymContext, type GymStatus } from '../contexts/GymContext'

// Time-dependent logic lives in a module-level helper (not the component body)
// so the reads of the clock aren't flagged as impure calls during render.
type BannerState =
  | { kind: 'none' }
  | { kind: 'trial'; daysLeft: number | null }
  | { kind: 'blocked' }

function bannerState(status: GymStatus, trialEndsAt: string | null): BannerState {
  if (status === 'active') return { kind: 'none' }
  const now = Date.now()
  const trialActive = status === 'trial' && (!trialEndsAt || new Date(trialEndsAt).getTime() > now)
  if (trialActive) {
    const daysLeft = trialEndsAt
      ? Math.max(0, Math.ceil((new Date(trialEndsAt).getTime() - now) / 86_400_000))
      : null
    return { kind: 'trial', daysLeft }
  }
  return { kind: 'blocked' }
}

// Full-width banner shown at the top of the gym app when the gym isn't a plain
// active gym: a warning while a trial is running (with days left), and a danger
// message when suspended, cancelled, or the trial has lapsed — which is also why
// writes come back 402 (GYM_SUSPENDED) from the Worker.
export function GymStandingBanner() {
  const { status, trialEndsAt } = useGymContext()
  const { t } = useTranslation()

  const state = bannerState(status, trialEndsAt)
  if (state.kind === 'none') return null

  if (state.kind === 'trial') {
    const message =
      state.daysLeft === null
        ? t('banner.trial')
        : t('banner.trialDaysLeft', { count: state.daysLeft })
    return (
      <div
        role="status"
        className="border-b border-warning-200 bg-warning-50 px-4 py-2 text-center text-sm font-medium text-warning-700"
      >
        {message}
      </div>
    )
  }

  // suspended, cancelled, or expired trial
  return (
    <div
      role="alert"
      className="border-b border-danger-200 bg-danger-50 px-4 py-2 text-center text-sm font-medium text-danger-700"
    >
      {t('banner.suspended')}
    </div>
  )
}
